<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'vuex';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '@/firebase';
import { getWidgetMetadata } from '@/components/widgets';
import { localToUTC } from '@/tools/TimeFunctions';
import Data from '@/api/data';
import Event from '@/api/event';

import DefaultLayout from '@/layouts/Layout.vue';
import GridCanvas from '@/components/dashboard/GridCanvas.vue';
import TimeseriesPageView from '@/components/dashboard/TimeseriesPageView.vue';
import WidgetConfigModal from '@/components/modals/WidgetConfigModal.vue';
import XetaSense from '@/apps/XetaSense.vue';
import BluetoothDash from '@/apps/TdwlbSensor.vue';
import { defaultUnitFallbackPath } from '@/tools/cloudAppUnit';
import { hasPermission } from '@/tools/PermissionChecker';

// Props
const route = useRoute();
const router = useRouter();
const store = useStore();

// State
const loading = ref(true);
const currentPage = ref(null);
const template = ref(null);
const showWidgetModal = ref(false);
const editingWidgetId = ref(null);
const editingWidget = ref(null);
const streamData = ref({}); // Centralized data for all streams
const activeStreams = ref({}); // Track active streams by source
const streamStartInFlight = new Set(); // Prevent duplicate concurrent starts
const streamSessionId = ref(0); // Used to invalidate in-flight stream starts
const streamReconnectTimeouts = ref({}); // Track scheduled reconnects by source
const streamReconnectAttempts = ref({}); // Track reconnect attempts for backoff
const historicalData = ref({}); // historical docs per source (for timeseries/charts)
const historicalLoading = ref({}); // { [source]: true } while that source's data is being fetched
const sourceErrors = ref({}); // { [source]: { type: 'not_found' } } for permanently unavailable sources
const isLiveMode = ref(true); // true = live/default 24h view, false = custom date range
const customDateRange = ref(null); // { start, end } when in historical mode
const isManualRefreshInProgress = ref(false);
const manualRefreshError = ref('');
const MAX_STREAM_RECONNECT_DELAY_MS = 30000;
const INITIAL_STREAM_RECONNECT_DELAY_MS = 1000;
const canEditDashboard = computed(() => hasPermission('editDashboard'));
const dashboardMode = ref('view');

const getTimestampMs = (doc) => {
  const raw = doc?.messageUTC;
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : ms;
};

const getMostRecentDoc = (docs) => {
  if (!Array.isArray(docs) || docs.length === 0) return null;
  return docs.reduce((latest, doc) => {
    const docMs = getTimestampMs(doc);
    const latestMs = getTimestampMs(latest);
    if (docMs == null) return latest;
    if (latestMs == null || docMs > latestMs) return doc;
    return latest;
  }, null);
};

const shouldAcceptDoc = (source, incomingDoc) => {
  const currentDoc = streamData.value?.[source]?.lastValue;
  const incomingMs = getTimestampMs(incomingDoc);
  const currentMs = getTimestampMs(currentDoc);

  if (incomingMs == null) return currentMs == null;
  if (currentMs == null) return true;
  return incomingMs >= currentMs;
};

const upsertLatestStreamDoc = (source, doc) => {
  if (!doc || !shouldAcceptDoc(source, doc)) return;
  streamData.value = {
    ...streamData.value,
    [source]: {
      lastValue: doc,
      lastUpdate: doc.messageUTC || streamData.value?.[source]?.lastUpdate || null
    }
  };
};

const clearReconnectTimeout = (source) => {
  const timeoutId = streamReconnectTimeouts.value[source];
  if (!timeoutId) return;
  clearTimeout(timeoutId);
  const updated = { ...streamReconnectTimeouts.value };
  delete updated[source];
  streamReconnectTimeouts.value = updated;
};

const clearReconnectAttempt = (source) => {
  if (!(source in streamReconnectAttempts.value)) return;
  const updated = { ...streamReconnectAttempts.value };
  delete updated[source];
  streamReconnectAttempts.value = updated;
};

// Computed
const dataID = computed(() => route.params.dataID);
const pageId = computed(() => route.params.pageId);
const pageType = computed(() => currentPage.value?.type);
const templateId = computed(() => {
  // Try to get from template object first
  if (template.value?.id) return template.value.id;
  // Fallback to device's defaultTemplate
  const device = store.state.device;
  return device?.defaultTemplate || null;
});

// Get all unique data sources needed by widgets on the page
const requiredSources = computed(() => {
  const sources = new Set();
  // Grid/custom pages: widgets
  if (currentPage.value?.widgets) {
    Object.values(currentPage.value.widgets).forEach(widget => {
      if (widget.source) {
        sources.add(widget.source);
      }
      // Handle timeseries widgets with multiple sources
      if (widget.specifications) {
        Object.keys(widget.specifications).forEach(source => sources.add(source));
      }
      // Handle events widgets - stream each variable source
      if (widget.type === 'events' && widget.values) {
        widget.values.forEach(v => { if (v.source) sources.add(v.source); });
      }
    });
  }
  // Timeseries pages: charts (each chart has specifications keyed by source)
  if (currentPage.value?.charts) {
    currentPage.value.charts.forEach(chart => {
      if (chart.specifications) {
        Object.keys(chart.specifications).forEach(source => sources.add(source));
      }
    });
  }
  return Array.from(sources);
});

const showStreamFallbackNotice = computed(() => {
  return requiredSources.value.some((source) => {
    const activeStream = activeStreams.value[source];
    const hasActive = Boolean(activeStream);
    const isClosed = hasActive && activeStream.readyState === 2;
    const hasPendingReconnect = Boolean(streamReconnectTimeouts.value[source]);
    const hasFailedBefore = (streamReconnectAttempts.value[source] || 0) > 0;
    if (isClosed) return true;
    return hasPendingReconnect || (!hasActive && hasFailedBefore);
  });
});
const isSseHealthy = computed(() => !showStreamFallbackNotice.value);

// Lifecycle
onMounted(async () => {
  await loadPageData();
  await loadHistoricalData();
  await setupStreams();
});

onUnmounted(() => {
  cleanupStreams();
});

onBeforeRouteLeave(() => {
  cleanupStreams();
});

// Watch for route changes (when navigating between pages)
watch([dataID, pageId], async () => {
  cleanupStreams();
  await loadPageData();
  await loadHistoricalData();
  await setupStreams();
});

// Watch for template changes in store
watch(() => store.state.template, (newTemplate) => {
  if (newTemplate) {
    template.value = newTemplate;
    loadPageData();
  }
}, { deep: true });

const resolveDashboardMode = (rawMode) => (rawMode === 'edit' ? 'edit' : 'view');

const syncDashboardModeQuery = async (mode) => {
  const nextMode = resolveDashboardMode(mode);
  const currentMode = resolveDashboardMode(route.query.mode);
  if (nextMode === currentMode) return;

  const nextQuery = { ...route.query };
  if (nextMode === 'edit') {
    nextQuery.mode = 'edit';
  } else {
    delete nextQuery.mode;
  }

  await router.replace({
    path: route.path,
    query: nextQuery
  });
};

const setDashboardMode = async (mode) => {
  if (!canEditDashboard.value && mode === 'edit') return;
  dashboardMode.value = resolveDashboardMode(mode);
  await syncDashboardModeQuery(dashboardMode.value);
};

watch(() => route.query.mode, (rawMode) => {
  const nextMode = resolveDashboardMode(rawMode);
  if (nextMode === 'edit' && !canEditDashboard.value) {
    dashboardMode.value = 'view';
    syncDashboardModeQuery('view');
    return;
  }
  dashboardMode.value = nextMode;
}, { immediate: true });

// Watch for changes in required sources
watch(requiredSources, async (newSources, oldSources) => {
  // Find sources to add
  const toAdd = newSources.filter(s => !oldSources?.includes(s));
  // Find sources to remove
  const toRemove = oldSources?.filter(s => !newSources.includes(s)) || [];
  
  // Remove unused streams
  toRemove.forEach(source => {
    if (activeStreams.value[source]) {
      activeStreams.value[source].close();
      delete activeStreams.value[source];
      delete streamData.value[source];
    }
    clearReconnectTimeout(source);
    clearReconnectAttempt(source);
  });
  
  // Add new streams
  for (const source of toAdd) {
    await startStream(source);
  }
  
  // Load historical data for newly added sources
  if (toAdd.length > 0) {
    await loadHistoricalData(toAdd);
  }
});

// Methods
const loadPageData = async () => {
  loading.value = true;
  
  // Guard against null/invalid pageId
  if (!pageId.value || pageId.value === 'null' || pageId.value === 'undefined') {
    console.error('Invalid pageId in route:', pageId.value, '- Redirecting to management');
    router.push(defaultUnitFallbackPath(dataID.value, store.state.device));
    loading.value = false;
    return;
  }
  
  // Get template from Vuex store
  template.value = store.state.template;
  
  // Ensure template has an id property
  if (template.value && !template.value.id) {
    console.warn('Template from store is missing id property, attempting to get from device details');
    const device = store.state.device;
    if (device?.defaultTemplate) {
      template.value.id = device.defaultTemplate;
    }
  }
  
  // Find the page by ID
  if (template.value?.pages) {
    currentPage.value = template.value.pages.find(p => p.id === pageId.value);
    
    // If page not found, redirect to management
    if (!currentPage.value) {
      console.error('Page not found for pageId:', pageId.value, '- Redirecting to management');
      router.push(defaultUnitFallbackPath(dataID.value, store.state.device));
      loading.value = false;
      return;
    }
  } else {
    // No pages array in template, redirect to portal / management
    console.warn('Template has no pages array - Redirecting to management');
    router.push(defaultUnitFallbackPath(dataID.value, store.state.device));
    loading.value = false;
    return;
  }
  
  loading.value = false;
};

// Historical data for timeseries/charts - dashboard owns all data fetching
// When dateRange is provided ({ start, end }), fetches that range; otherwise defaults to last 24h
const loadHistoricalData = async (sourcesToLoad, dateRange) => {
  const sources = sourcesToLoad ?? requiredSources.value;
  if (sources.length === 0) return;
  
  // Mark every source as loading up front so widgets show spinners immediately
  const loadingUpdate = { ...historicalLoading.value };
  sources.forEach(s => { loadingUpdate[s] = true; });
  historicalLoading.value = loadingUpdate;

  try {
    const token = await auth.currentUser.getIdToken();

    let startDate, endDate;
    if (dateRange) {
      startDate = new Date(dateRange.start);
      endDate = new Date(dateRange.end);
    } else {
      endDate = new Date();
      startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
    }

    // Fetch all sources in parallel so each chart clears its spinner as soon as its own data arrives
    await Promise.all(sources.map(async (source) => {
      try {
        const body = {
          dataID: dataID.value,
          subject: source,
          filters: [
            { field: 'messageUTC', operator: '>=', value: localToUTC(startDate) },
            { field: 'messageUTC', operator: '<=', value: localToUTC(endDate) }
          ],
          sort: [{ field: 'messageUTC', direction: 'asc' }]
        };
        const response = await Data.post(body, token);
        historicalData.value = {
          ...historicalData.value,
          [source]: response && response.length > 0 ? response : []
        };
      } catch (err) {
        if (err.isSourceNotFound) {
          sourceErrors.value = { ...sourceErrors.value, [source]: { type: 'not_found' } };
        } else {
          console.error(`Error loading historical data for ${source}:`, err);
        }
        historicalData.value = { ...historicalData.value, [source]: [] };
      } finally {
        // Clear this source's spinner as soon as it resolves (success or error)
        const updated = { ...historicalLoading.value };
        delete updated[source];
        historicalLoading.value = updated;
      }
    }));
  } catch (err) {
    console.error('Error loading historical data:', err);
  }
};

const fetchLatestDocForSource = async (source, token) => {
  const body = {
    dataID: dataID.value,
    subject: source,
    limit: 1,
    sort: [{ field: 'messageUTC', direction: 'desc' }]
  };
  const response = await Data.post(body, token);
  return response && response.length > 0 ? getMostRecentDoc(response) : null;
};

const scheduleStreamReconnect = (source, sessionIdAtStart) => {
  if (sourceErrors.value[source]) return;
  if (!source) return;
  if (sessionIdAtStart !== streamSessionId.value) return;
  if (!requiredSources.value.includes(source)) return;
  if (activeStreams.value[source]) return;
  if (streamStartInFlight.has(source)) return;
  if (streamReconnectTimeouts.value[source]) return;

  const attempt = (streamReconnectAttempts.value[source] || 0) + 1;
  const delay = Math.min(
    INITIAL_STREAM_RECONNECT_DELAY_MS * (2 ** (attempt - 1)),
    MAX_STREAM_RECONNECT_DELAY_MS
  );
  streamReconnectAttempts.value = {
    ...streamReconnectAttempts.value,
    [source]: attempt
  };

  const timeoutId = setTimeout(async () => {
    clearReconnectTimeout(source);
    if (sessionIdAtStart !== streamSessionId.value) return;
    if (!requiredSources.value.includes(source)) return;
    await startStream(source, { fetchLatestSnapshot: false });
  }, delay);

  streamReconnectTimeouts.value = {
    ...streamReconnectTimeouts.value,
    [source]: timeoutId
  };
};

const refreshDashboardData = async () => {
  if (isManualRefreshInProgress.value) return;
  isManualRefreshInProgress.value = true;
  manualRefreshError.value = '';

  try {
    const token = await auth.currentUser.getIdToken();
    const sources = [...requiredSources.value];

    await Promise.all(
      sources.map(async (source) => {
        try {
          const latestDoc = await fetchLatestDocForSource(source, token);
          if (latestDoc) upsertLatestStreamDoc(source, latestDoc);
        } catch (error) {
          console.error(`Error refreshing latest value for ${source}:`, error);
        }
      })
    );

    await loadHistoricalData(
      undefined,
      isLiveMode.value ? undefined : customDateRange.value
    );
  } catch (error) {
    console.error('Manual dashboard refresh failed:', error);
    manualRefreshError.value = 'Refresh failed. Please try again.';
  } finally {
    isManualRefreshInProgress.value = false;
  }
};

// Handle date range selection from TimeseriesPageView
const handleDateRangeChange = async (range) => {
  if (range) {
    // Switch to historical mode with the selected range
    isLiveMode.value = false;
    customDateRange.value = range;
    historicalData.value = {};
    await loadHistoricalData(undefined, range);
  } else {
    // Return to live mode - reload default 24h data
    isLiveMode.value = true;
    customDateRange.value = null;
    historicalData.value = {};
    await loadHistoricalData();
  }
};

// Stream management
const startStream = async (source, options = {}) => {
  if (sourceErrors.value[source]) return;
  const { fetchLatestSnapshot = true } = options;
  const sessionIdAtStart = streamSessionId.value;
  try {
    if (!source || !dataID.value) return;
    if (activeStreams.value[source]) return;
    if (streamStartInFlight.has(source)) return;
    streamStartInFlight.add(source);

    const token = await auth.currentUser.getIdToken();
    if (sessionIdAtStart !== streamSessionId.value) return;
    
    // Initialize this source only if it has not been seen yet
    if (!streamData.value[source]) {
      streamData.value = {
        ...streamData.value,
        [source]: { lastValue: null, lastUpdate: null }
      };
    }
    
    // Fetch latest snapshot only for initial/manual starts.
    // Reconnect attempts skip this to avoid repeated REST charges.
    if (fetchLatestSnapshot) {
      const latestDoc = await fetchLatestDocForSource(source, token);
      if (sessionIdAtStart !== streamSessionId.value) return;
      if (latestDoc) upsertLatestStreamDoc(source, latestDoc);
    }
    
    // Start the stream
    console.log('Starting stream for:', source);
    const eventSource = await Data.stream(dataID.value, source, token);

    // If we navigated away / switched page while this async call was in flight, close immediately.
    if (sessionIdAtStart !== streamSessionId.value) {
      eventSource.close();
      return;
    }
    activeStreams.value[source] = eventSource;
    clearReconnectTimeout(source);
    clearReconnectAttempt(source);
    
    eventSource.addEventListener('added', async (event) => {
      try {
        const addedData = JSON.parse(event.data);
        if (addedData && addedData.length > 0) {
          const latestDoc = getMostRecentDoc(addedData);
          upsertLatestStreamDoc(source, latestDoc);
        }
      } catch (error) {
        console.error(`Error parsing stream data for ${source}:`, error);
      }
    });
    
    eventSource.onerror = (error) => {
      console.error(`Stream error for ${source}:`, error);
      if (activeStreams.value[source]) {
        activeStreams.value[source].close();
        delete activeStreams.value[source];
      }
      scheduleStreamReconnect(source, sessionIdAtStart);
    };
  } catch (error) {
    if (error.isSourceNotFound) {
      sourceErrors.value = { ...sourceErrors.value, [source]: { type: 'not_found' } };
    } else {
      console.error(`Error starting stream for ${source}:`, error);
      scheduleStreamReconnect(source, sessionIdAtStart);
    }
  } finally {
    streamStartInFlight.delete(source);
  }
};

const setupStreams = async () => {
  await Promise.all(requiredSources.value.map(source => startStream(source)));
};

const cleanupStreams = () => {
  streamSessionId.value += 1;
  streamStartInFlight.clear();
  Object.keys(streamReconnectTimeouts.value).forEach((source) => {
    clearReconnectTimeout(source);
  });
  streamReconnectAttempts.value = {};
  Object.values(activeStreams.value).forEach(stream => {
    if (stream) stream.close();
  });
  activeStreams.value = {};
  streamData.value = {};
  historicalData.value = {};
  sourceErrors.value = {};
};

// Widget handlers
const handleAddWidget = () => {
  editingWidgetId.value = null;
  editingWidget.value = null;
  showWidgetModal.value = true;
};

const handleEditWidget = (widgetId) => {
  editingWidgetId.value = widgetId;
  
  // For timeseries pages, find the chart
  if (pageType.value === 'timeseries') {
    editingWidget.value = currentPage.value.charts?.find(c => c.id === widgetId) || null;
  } else {
    editingWidget.value = currentPage.value.widgets?.[widgetId] || null;
  }
  
  showWidgetModal.value = true;
};

const handleDeleteWidget = async (widgetId) => {
  console.log('Deleted widget:', widgetId);
  // Reload page data from store after deletion
  await loadPageData();
};

const handleWidgetSave = async ({ widgetId, widget }) => {
  // For timeseries pages, handle charts differently
  if (pageType.value === 'timeseries') {
    await handleChartSave({ chartId: widgetId, chart: widget });
    return;
  }
  
  // Generate new widget ID if adding
  const finalWidgetId = widgetId || `widget-${Date.now()}`;
  
  // Close modal immediately for better UX
  showWidgetModal.value = false;
  
  // If editing, do everything synchronously
  if (widgetId) {
    try {
      const updatedPage = await saveWidgetToFirestore(finalWidgetId, widget);
      currentPage.value = updatedPage;
    } catch (err) {
      console.error('Error saving widget:', err);
      alert('Failed to save widget. Please try again.');
    }
    return;
  }
  
  // For new widgets: optimistically add to UI immediately (except events - see below)
  const metadata = getWidgetMetadata(widget.type);
  const defaultSize = metadata?.defaultSize || { w: 4, h: 6 };
  
  // Calculate Y position - place below existing widgets
  let maxY = 0;
  currentPage.value.layout.forEach(item => {
    const itemBottom = item.y + item.h;
    if (itemBottom > maxY) maxY = itemBottom;
  });
  
  const newLayoutItem = {
    i: finalWidgetId,
    x: 0,
    y: maxY,
    w: defaultSize.w,
    h: defaultSize.h,
    minW: 2,
    minH: 2
  };

  try {
    // Events widget: POST first, then add to UI so EventsTable only mounts after event exists
    if (widget.type === 'events') {
      const eventBody = {
        dataID: dataID.value,
        name: widget.source,
        comparison: widget.comparison,
        startDate: widget.startDate,
        values: widget.values,
      };

      const response = await Event.post('', eventBody);
      console.log('Event created:', response);
      if (response.status !== 'success') {
        throw new Error('Failed to create event on backend');
      }

      // Add widget only after event exists; table will do GET only on mount
      currentPage.value.widgets[finalWidgetId] = { ...widget, _eventJustCreated: true };
      currentPage.value.layout.push(newLayoutItem);
    } else {
      // Non-events: add to UI immediately
      currentPage.value.widgets[finalWidgetId] = { ...widget, _loading: true };
      currentPage.value.layout.push(newLayoutItem);
    }
    
    // Save to Firestore and get the updated page
    const updatedPage = await saveWidgetToFirestore(finalWidgetId, widget);
    
    // Update currentPage with the saved data (this will have the widget without _loading / _eventJustCreated)
    currentPage.value = updatedPage;
  } catch (err) {
    console.error('Error saving widget:', err);
    
    // Remove the widget if it was added to the UI
    delete currentPage.value.widgets[finalWidgetId];
    const layoutIndex = currentPage.value.layout.findIndex(item => item.i === finalWidgetId);
    if (layoutIndex !== -1) {
      currentPage.value.layout.splice(layoutIndex, 1);
    }
    
    alert('Failed to create widget. Please try again.');
  }
};

const saveWidgetToFirestore = async (widgetId, widget) => {
  // Use current in-memory state to preserve recent changes (like deletions)
  const templateRef = doc(db, 'templates', template.value.id);
  
  // Deep copy the current page data
  const updatedPage = {
    ...currentPage.value,
    layout: [...(currentPage.value.layout || [])],
    widgets: { ...(currentPage.value.widgets || {}) }
  };
  
  // Add or update widget config (without layout info and loading state)
  const cleanWidget = { ...widget };
  delete cleanWidget._loading;
  delete cleanWidget._eventJustCreated;
  updatedPage.widgets[widgetId] = cleanWidget;
  
  // Add or update layout position
  const existingLayoutIndex = updatedPage.layout.findIndex(item => item.i === widgetId);
  if (existingLayoutIndex === -1) {
    // New widget - use current page's layout
    const currentLayoutItem = currentPage.value.layout.find(item => item.i === widgetId);
    if (currentLayoutItem) {
      updatedPage.layout.push({ ...currentLayoutItem });
    }
  }
  
  // Fetch fresh template to update only our page while preserving other pages
  const templateDoc = await getDoc(templateRef);
  
  if (!templateDoc.exists()) {
    throw new Error('Template not found');
  }
  
  const templateData = templateDoc.data();
  
  // Update Firestore - replace only our page, keep others unchanged
  const updatedPages = templateData.pages.map(p => 
    p.id === currentPage.value.id ? updatedPage : p
  );
  
  await updateDoc(templateRef, { pages: updatedPages });
  
  // Update the store with the new template data (preserve id and dataID)
  const updatedTemplateData = { 
    ...templateData, 
    pages: updatedPages,
    id: templateId.value,
    dataID: dataID.value
  };
  await store.dispatch('setTemplate', updatedTemplateData);
  
  // Return the updated page so it can be used to update currentPage
  return updatedPage;
};

const handleChartSave = async ({ chartId, chart }) => {
  // Generate new chart ID if adding
  const finalChartId = chartId || `chart-${Date.now()}`;
  
  // Close modal immediately for better UX
  showWidgetModal.value = false;
  
  try {
    const templateRef = doc(db, 'templates', template.value.id);
    
    // Fetch fresh template to preserve other pages
    const templateDoc = await getDoc(templateRef);
    
    if (!templateDoc.exists()) {
      throw new Error('Template not found');
    }
    
    const templateData = templateDoc.data();
    
    // Deep copy the current page data
    const updatedPage = {
      ...currentPage.value,
      charts: [...(currentPage.value.charts || [])]
    };
    
    // Add chart with id
    const chartWithId = { ...chart, id: finalChartId };
    
    if (chartId) {
      // Update existing chart
      const chartIndex = updatedPage.charts.findIndex(c => c.id === chartId);
      if (chartIndex !== -1) {
        updatedPage.charts[chartIndex] = chartWithId;
      }
    } else {
      // Add new chart
      updatedPage.charts.push(chartWithId);
    }
    
    // Update Firestore - replace only our page, keep others unchanged
    const updatedPages = templateData.pages.map(p => 
      p.id === currentPage.value.id ? updatedPage : p
    );
    
    await updateDoc(templateRef, { pages: updatedPages });
    
    // Update the store with the new template data (preserve id and dataID)
    const updatedTemplateData = { 
      ...templateData, 
      pages: updatedPages,
      id: templateId.value,
      dataID: dataID.value
    };
    await store.dispatch('setTemplate', updatedTemplateData);
    
    // Update currentPage with the saved data
    currentPage.value = updatedPage;
  } catch (err) {
    console.error('Error saving chart:', err);
    alert('Failed to save chart. Please try again.');
  }
};
</script>

<template>
  <DefaultLayout :dataID="dataID">
    <div class="flex flex-col w-full h-full min-h-0 p-4 overflow-hidden">
      <div v-if="loading" class="flex items-center justify-center h-full">
        <p class="font-body text-tier-3 text-ink-muted">Loading page...</p>
      </div>

      <div v-else-if="!currentPage" class="flex items-center justify-center h-full">
        <p class="font-body text-tier-3 text-mc-red">Page not found</p>
      </div>
      
      <div v-else class="flex flex-col flex-1 min-h-0">
        <div class="mb-3 hidden sm:flex items-center justify-between gap-3">
          <p class="hidden sm:block font-mono text-tier-2 font-medium uppercase tracking-[0.14em] text-ink-muted dark:text-on-dark-muted">
            {{ currentPage?.name }}
          </p>
          <div class="inline-flex rounded-mc-sm bg-mc-cloud dark:bg-white/10 p-1" role="group" aria-label="Dashboard mode">
            <button
              type="button"
              class="px-3 py-1.5 font-body font-bold text-sm rounded-mc-sm transition-colors duration-160"
              :class="dashboardMode === 'view'
                ? 'bg-mc-white dark:bg-white/15 text-mc-ink dark:text-on-dark border border-mc-steel dark:border-white/20 shadow-mc-sm'
                : 'text-ink-muted dark:text-on-dark-muted hover:bg-mc-white/60 dark:hover:bg-white/10'"
              @click="setDashboardMode('view')"
            >
              View
            </button>
            <button
              type="button"
              class="px-3 py-1.5 font-body font-bold text-sm rounded-mc-sm transition-colors duration-160"
              :class="dashboardMode === 'edit'
                ? 'bg-mc-white dark:bg-white/15 text-mc-ink dark:text-on-dark border border-mc-steel dark:border-white/20 shadow-mc-sm'
                : 'text-ink-muted dark:text-on-dark-muted hover:bg-mc-white/60 dark:hover:bg-white/10'"
              :disabled="!canEditDashboard"
              :title="canEditDashboard ? 'Switch to layout editing mode' : 'You do not have permission to edit dashboard layout'"
              @click="setDashboardMode('edit')"
            >
              Edit Layout
            </button>
          </div>
        </div>

        <div
          v-if="showStreamFallbackNotice"
          class="mb-3 flex flex-wrap items-start sm:items-center gap-2 rounded-mc-sm border border-mc-steel bg-mc-cloud px-3 py-2"
        >
          <p class="flex-1 min-w-[240px] font-body text-tier-1 text-mc-ink">
            Live updates are temporarily unavailable on this device. You can continue using cached/recent data, and this page will keep retrying the stream in the background.
            If this persists, disable VPN/browser extensions, or ask IT to allow
            <span class="font-semibold">api.meshcommengineering.com/api/v1/stream/data/*</span>.
          </p>
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-2 font-body font-bold text-sm rounded-mc-sm border border-mc-steel bg-mc-white text-mc-ink hover:bg-mc-cloud transition-colors duration-160 shadow-mc-sm shrink-0 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isManualRefreshInProgress"
            @click="refreshDashboardData"
            title="Refresh latest data from API"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992V4.356" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.977 14.652H7.97v4.992" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.51 9a9 9 0 0114.13-3.36L21.015 9" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M20.49 15a9 9 0 01-14.13 3.36L2.985 15" />
            </svg>
            <span class="hidden sm:inline">{{ isManualRefreshInProgress ? 'Refreshing...' : 'Refresh Data' }}</span>
            <span class="sm:hidden">Refresh</span>
          </button>
        </div>
        <p v-if="showStreamFallbackNotice && manualRefreshError" class="mb-3 font-body text-tier-1 text-mc-red">{{ manualRefreshError }}</p>

        <!-- Custom page with GridCanvas -->
        <div v-if="pageType === 'custom'" class="flex-1 min-h-0 overflow-y-auto">
          <GridCanvas
            :dataID="dataID"
            :page="currentPage"
            :templateId="templateId"
            :timeFormat="store.state.user?.timeFormat || '24hour'"
            :streamData="streamData"
            :historicalData="historicalData"
            :sourceErrors="sourceErrors"
            :dashboardMode="dashboardMode"
            :showEditModeToggle="false"
            @addWidget="handleAddWidget"
            @editWidget="handleEditWidget"
            @deleteWidget="handleDeleteWidget"
          />
        </div>
        
        <div v-else-if="pageType === 'timeseries'" class="flex-1 min-h-0 overflow-hidden">
          <TimeseriesPageView
            :dataID="dataID"
            :page="currentPage"
            :templateId="templateId"
            :timeFormat="store.state.user?.timeFormat || '24hour'"
            :streamData="streamData"
            :historicalData="historicalData"
            :historicalLoading="historicalLoading"
            :isLiveMode="isLiveMode"
            :isSseHealthy="isSseHealthy"
            :sourceErrors="sourceErrors"
            :dashboardMode="dashboardMode"
            :showEditModeToggle="false"
            @addChart="handleAddWidget"
            @editChart="handleEditWidget"
            @deleteChart="handleDeleteWidget"
            @dateRangeChange="handleDateRangeChange"
          />
        </div>
        
        <div v-else-if="pageType === 'xetasense'">
          <XetaSense :embedded="true" />
        </div>
        
        <div v-else-if="pageType === 'pressure-sensor'">
          <BluetoothDash :embedded="true" />
        </div>
        
        <div v-else class="bg-mc-red-100 p-8 rounded-mc-md">
          <p class="font-body text-tier-3 text-mc-red">Unknown page type: {{ pageType }}</p>
        </div>
      </div>
    </div>

    <!-- Widget Config Modal -->
    <WidgetConfigModal
      v-if="showWidgetModal"
      :widgetId="editingWidgetId"
      :existingWidget="editingWidget"
      :dataID="dataID"
      :pageType="pageType"
      @close="showWidgetModal = false"
      @save="handleWidgetSave"
    />
  </DefaultLayout>
</template>
